import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {Subscription} from 'rxjs/Subscription';
import 'rxjs/add/observable/timer';
import 'rxjs/add/operator/switchMap';
import {ReportProgress} from '../models/report.model';
import {ReportService} from '../services/report.service';
import {
  REPORT_PROGRESS_ID_PREFIX, REPORT_PROGRESS_ID_RADIX, REPORT_PROGRESS_ID_SEPARATOR,
  REPORT_PROGRESS_POLL_INITIAL_DELAY_MILLIS, REPORT_PROGRESS_POLL_MILLIS,
  REPORT_PROGRESS_RANDOM_END, REPORT_PROGRESS_RANDOM_START
} from './report-progress.constants';
import {ReportProgressState} from './report-progress.state';
import {ReportProgressView} from './report-progress.types';

@Injectable()
export class ReportProgressCoordinator {
  private progressId: string;
  private subscription: Subscription;

  constructor(private source: ReportService, private state: ReportProgressState) {
  }

  startPersonal(
    view: ReportProgressView, resultHandler: (successful: boolean) => void
  ): void {
    this.reset(view);
    this.progressId = this.createProgressId();
    this.poll(view);
    this.source.trackNextReportProgress(this.progressId, resultHandler);
  }

  startGroup(view: ReportProgressView): void {
    this.reset(view);
    this.progressId = this.createProgressId();
    this.poll(view);
  }

  currentId(): string {
    return this.progressId;
  }

  cancelPersonal(view: ReportProgressView): void {
    this.source.cancelNextReportProgress();
    this.fail(view);
  }

  complete(view: ReportProgressView): void {
    this.state.complete(view);
    this.stop();
  }

  fail(view: ReportProgressView): void {
    this.state.fail(view);
    this.stop();
  }

  destroy(): void {
    // The owning component calls destroy() from its OnDestroy lifecycle.
    this.stop();
  }

  stop(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  private reset(view: ReportProgressView): void {
    this.stop();
    this.progressId = null;
    this.state.reset(view);
  }

  private poll(view: ReportProgressView): void {
    this.subscription = Observable.timer(
      REPORT_PROGRESS_POLL_INITIAL_DELAY_MILLIS, REPORT_PROGRESS_POLL_MILLIS)
      .switchMap(() => this.source.getReportProgress(this.progressId))
      .subscribe(
        (progress: ReportProgress) => this.apply(view, progress),
        () => this.handleUnavailable(view)
      );
  }

  private apply(view: ReportProgressView, progress: ReportProgress): void {
    if (this.state.apply(view, progress)) {
      this.stop();
    }
  }

  private handleUnavailable(view: ReportProgressView): void {
    this.state.markUnavailable(view);
    this.stop();
  }

  private createProgressId(): string {
    const timestamp = new Date().getTime().toString(REPORT_PROGRESS_ID_RADIX);
    const random = Math.random().toString(REPORT_PROGRESS_ID_RADIX)
      .substring(REPORT_PROGRESS_RANDOM_START, REPORT_PROGRESS_RANDOM_END);
    return REPORT_PROGRESS_ID_PREFIX + timestamp + REPORT_PROGRESS_ID_SEPARATOR + random;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/modals/report-progress.coordinator.ts